import { useMemo, useState } from 'react';
import Lottie from 'lottie-react';
import useLottieJson from '../../hooks/useLottieJson';
import PromoProductCard from './PromoProductCard';
import PromoTabs from './PromoTabs';
import { estaDisponible } from '../../utils/disponibilidad';
import { vibrar } from '../../utils/vibrar';
import { useEntradaHome } from '../../hooks/useEntradaHome';

const buildImageUrl = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `https://${url}`;
};

/**
 * Bloque visual a ancho completo: Lottie animado o imagen estática, según
 * `type`. El Lottie reserva el alto por su proporción natural para no provocar
 * salto de layout; la imagen lo hace sola con `h-auto`.
 */
function Bloque({ url, type }) {
  const esLottie = type !== 'image';
  const anim = useLottieJson(esLottie ? url : null);

  if (!url) return null;

  if (!esLottie) {
    return <img src={buildImageUrl(url)} alt="" className="w-full h-auto block" loading="lazy" />;
  }

  if (!anim) return null;
  const w = anim.w || 1080;
  const h = anim.h || 400;

  return (
    <div className="w-full" style={{ aspectRatio: `${w} / ${h}` }}>
      <Lottie animationData={anim} loop autoplay className="w-full h-full" />
    </div>
  );
}

/**
 * Sección promocional del home, armada por BLOQUES OPCIONALES. Cada bloque se
 * pinta solo si está configurado, así que las distintas vistas salen de
 * combinarlos, no de un "tipo" programado:
 *
 *   fondo → encabezado (Lottie) → pestañas → productos → promo (Lottie)
 *
 * Los productos se resuelven contra el menú que la página ya tiene cargado
 * (`todosLosProductos`), así que curar esta sección no cuesta ninguna petición
 * extra ni duplica datos del producto.
 *
 * Props:
 *   - config: Business.promoSection
 *   - todosLosProductos: todos los productos del menú, para resolver los ids
 *   - onProductClick: abre la hoja del producto
 *   - rellenoSuperior: px que la sección sube por detrás del buscador; se
 *     compensan con relleno para que el fondo llegue hasta ahí sin que el
 *     encabezado quede tapado.
 */
export default function PromoSection({ config, todosLosProductos = [], onProductClick, rellenoSuperior = 0 }) {
  const [tabActiva, setTabActiva] = useState(0);
  const entrada = useEntradaHome();

  // Tic al cambiar de pestaña (no al tocar la que ya está).
  const cambiarTab = (i) => {
    if (i !== tabActiva) vibrar();
    setTabActiva(i);
  };

  const porId = useMemo(
    () => new Map(todosLosProductos.map((p) => [String(p._id || p.id), p])),
    [todosLosProductos]
  );

  const tabs = useMemo(
    () => [...(config?.tabs || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [config?.tabs]
  );

  // Con pestañas manda la activa; sin pestañas, la lista suelta.
  const idsVisibles = tabs.length > 0
    ? (tabs[Math.min(tabActiva, tabs.length - 1)]?.products || [])
    : (config?.products || []);

  // El orden curado manda: se recorre la lista de ids, no la del menú. Los no
  // disponibles se saltan: es una vitrina, no el menú.
  const productos = idsVisibles.map((id) => porId.get(String(id))).filter((p) => p && estaDisponible(p));

  if (!config?.isActive) return null;

  const hayAlgo = config.headerLottie || tabs.length > 0 || productos.length > 0 || config.promoLottie;
  if (!hayAlgo) return null;

  return (
    <>
    <section
      className="relative bg-cover bg-center bg-promo-section-bg"
      style={{
        paddingTop: rellenoSuperior,
        ...(config.background ? { backgroundImage: `url(${buildImageUrl(config.background)})` } : {}),
      }}
    >
      {/* Al quitarse el splash, lo de adentro entra en cascada sobre el fondo
          (que ya está): encabezado, pestañas, tarjetas. */}
      {config.headerLottie && (
        <div {...entrada(0)}>
          <Bloque url={config.headerLottie} type={config.headerType} />
        </div>
      )}

      {tabs.length > 0 && (
        <div {...entrada(70)}>
          <PromoTabs tabs={tabs} activa={Math.min(tabActiva, tabs.length - 1)} onChange={cambiarTab} />
        </div>
      )}

      {/* Con pestañas, de la banda hacia abajo todo va sobre el panel: arriba
          el mismo verde con el que termina la pestaña activa (para que se
          fundan) y oscureciendo levemente hasta el final de la sección, como
          en la referencia. Así la carpeta se lee abierta. */}
      <div className={tabs.length > 0 ? 'bg-gradient-to-b from-promo-panel to-promo-panel-fin -mt-px' : ''}>
        {/* 12px entre tarjetas, 6px bajo las pestañas y 28px bajo el texto:
            medidos lado a lado con la app original. */}
        {productos.length > 0 && (
          <div className="flex gap-3 overflow-x-auto scrollbar-hide px-3 pt-1.5 pb-7">
            {productos.map((product, i) => (
              <div key={product._id || product.id} {...entrada(130 + i * 60, 'lado', 'flex-shrink-0 flex')}>
                <PromoProductCard
                  product={product}
                  bestsellerImage={config.bestsellerImage}
                  onClick={() => onProductClick?.(product)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>

    {/* Promo de abajo: fuera de la sección y a todo el ancho, montada a
        caballo sobre su orilla. La mitad de arriba va sobre el color con el
        que termina la sección (el panel con pestañas, o el fondo de la
        sección sin ellas) y la de abajo sobre el blanco de la página. Es una
        franja de color de media altura detrás, así no hay que medir nada. */}
    {config.promoLottie && (
      // -mt-0.5: 2px más arriba, pegándose un poco más a las tarjetas.
      <div className="relative -mt-0.5">
        <div
          aria-hidden="true"
          className={`absolute inset-x-0 top-0 h-1/2 ${tabs.length > 0 ? 'bg-promo-panel-fin' : 'bg-promo-section-bg'}`}
        />
        <div className="relative">
          <Bloque url={config.promoLottie} type={config.promoType} />
        </div>
      </div>
    )}
    </>
  );
}
